/**
 * Shared building blocks for request, offer and invoice pages: page layout
 * with header, primary action and side panel, a customer picker, related
 * document links, the status timeline, and PDF helpers.
 */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CustomerResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { apiGet, apiGetPage, apiPostBlob } from "../../lib/api.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import { useDebounced } from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { formatDate } from "../shared.js";

export function DocumentPage({
  back,
  title,
  number,
  status,
  meta,
  actions,
  children,
  aside,
}: {
  back: { to: string; label: string };
  title: ReactNode;
  number?: ReactNode;
  status?: string;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="pb-24 md:pb-0">
      <Link
        to={back.to}
        className="mb-3 inline-flex items-center gap-1 text-sm text-app-muted hover:text-app-text"
      >
        <i className="pi pi-arrow-left text-xs" aria-hidden="true" />
        {back.label}
      </Link>
      <header className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {number ? (
              <span className="font-mono text-sm text-app-muted">{number}</span>
            ) : null}
            {status ? <StatusBadge status={status} /> : null}
          </div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-app-text sm:text-2xl">
            {title}
          </h1>
          {meta ? (
            <div className="mt-1 text-sm text-app-muted">{meta}</div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </header>
      <div
        className={cx(
          "grid grid-cols-1 gap-4",
          aside ? "lg:grid-cols-[minmax(0,1fr)_20rem]" : "",
        )}
      >
        <div className="min-w-0 space-y-4">{children}</div>
        {aside ? <aside className="space-y-4">{aside}</aside> : null}
      </div>
    </div>
  );
}

export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "rounded-xl border border-app-border bg-app-surface p-4 sm:p-5",
        className,
      )}
    >
      {title || actions ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          {title ? (
            <h2 className="text-sm font-semibold text-app-text">{title}</h2>
          ) : (
            <span />
          )}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** Banner explaining why a document can't be edited and what to do. */
export function Notice({
  tone = "info",
  children,
  action,
}: {
  tone?: "info" | "warning" | "danger" | "success";
  children: ReactNode;
  action?: ReactNode;
}) {
  const styles = {
    info: "border-info-border bg-info-soft text-info",
    warning: "border-warning-border bg-warning-soft text-warning",
    danger: "border-danger-border bg-danger-soft text-danger",
    success: "border-success-border bg-success-soft text-success",
  }[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cx(
        "flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between",
        styles,
      )}
    >
      <div>{children}</div>
      {action}
    </div>
  );
}

export function RelatedLinks({
  items,
}: {
  items: Array<{
    label: string;
    to: string;
    number: string;
    status?: string;
  }>;
}) {
  const { t } = useTranslation();
  if (items.length === 0)
    return <p className="text-sm text-app-muted">{t("doc.noRelated")}</p>;
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.to} className="flex items-center justify-between gap-2">
          <Link to={item.to} className="min-w-0 text-sm hover:underline">
            <span className="text-app-muted">{item.label} </span>
            <span className="font-medium text-brand">{item.number}</span>
          </Link>
          {item.status ? <StatusBadge status={item.status} /> : null}
        </li>
      ))}
    </ul>
  );
}

interface HistoryEntry {
  id: string;
  from: string | null;
  to: string;
  comment: string | null;
  user: string | null;
  at: string;
}

export function StatusTimeline({
  entityType,
  id,
  version,
}: {
  entityType: "request" | "offer" | "invoice" | "project" | "customer";
  id: string;
  /** Refetch when the document changes. */
  version?: number;
}) {
  const { t } = useTranslation();
  const history = useQuery({
    queryKey: ["history", entityType, id, version],
    queryFn: () =>
      apiGet<{ data: HistoryEntry[] }>(`/history/${entityType}/${id}`).then(
        (response) => response.data,
      ),
  });
  if (!history.data?.length) return null;
  return (
    <Panel title={t("doc.history")}>
      <ol className="relative space-y-3 border-l border-app-border pl-4">
        {history.data.map((entry) => (
          <li key={entry.id} className="text-sm">
            <span
              className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-app-surface bg-brand"
              aria-hidden="true"
            />
            <div className="font-medium text-app-text">
              {t(`status.${entry.to}`, { defaultValue: entry.to })}
            </div>
            <div className="text-xs text-app-muted">
              {formatDateTime(entry.at)}
              {entry.user ? ` · ${entry.user}` : ""}
            </div>
            {entry.comment ? (
              <div className="mt-0.5 text-app-muted">{entry.comment}</div>
            ) : null}
          </li>
        ))}
      </ol>
    </Panel>
  );
}

export function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export { formatDate };

/** Open a PDF from a GET or POST endpoint in a new tab. */
export async function openPdf(path: string, method: "GET" | "POST" = "GET") {
  // Open the tab synchronously so pop-up blockers allow it.
  const tab = window.open("", "_blank");
  try {
    const blob =
      method === "POST"
        ? await apiPostBlob(path)
        : await fetch(`/api/v1${path}`, { credentials: "same-origin" }).then(
            (response) => {
              if (!response.ok) throw new Error(response.statusText);
              return response.blob();
            },
          );
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/**
 * Searchable customer combobox backed by the customer list API. Shows name,
 * number and city so homonyms can be told apart.
 */
export function CustomerPicker({
  value,
  onChange,
  disabled,
  invalid,
  id,
}: {
  value: CustomerResponse | null;
  onChange: (customer: CustomerResponse | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounced(query, 200);
  const root = useRef<HTMLDivElement>(null);
  const results = useQuery({
    queryKey: ["customers", "picker", debounced],
    queryFn: () =>
      apiGetPage<CustomerResponse>("/customers", {
        q: debounced || undefined,
        pageSize: 8,
      }),
    enabled: open,
  });
  const options = results.data?.data ?? [];
  useEffect(() => setActive(0), [debounced]);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (value) {
    return (
      <div
        className={cx(
          "flex items-center justify-between gap-2 rounded-lg border border-app-border bg-app-background px-3 py-2",
          disabled && "opacity-70",
        )}
      >
        <div className="min-w-0">
          <div className="truncate font-medium">{value.displayName}</div>
          <div className="truncate text-xs text-app-muted">
            {[
              value.number,
              value.billingAddress
                ? `${value.billingAddress.postalCode} ${value.billingAddress.city}`
                : null,
              value.phone,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        {disabled ? null : (
          <Button
            type="button"
            size="small"
            variant="text"
            severity="secondary"
            onClick={() => onChange(null)}
          >
            {t("common.change")}
          </Button>
        )}
      </div>
    );
  }

  const choose = (customer: CustomerResponse) => {
    onChange(customer);
    setQuery("");
    setOpen(false);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && open && options[active]) {
      event.preventDefault();
      choose(options[active]!);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };
  const listId = `${id ?? "customer"}-options`;
  return (
    <div ref={root} className="relative">
      <InputText
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        disabled={disabled}
        invalid={invalid}
        onFocus={() => setOpen(true)}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={t("customerPicker.placeholder")}
        className="w-full"
        autoComplete="off"
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
        >
          {results.isLoading ? (
            <li className="px-3 py-2 text-sm text-app-muted">
              {t("common.loading")}
            </li>
          ) : options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-app-muted">
              {t("customerPicker.empty")}
            </li>
          ) : (
            options.map((customer, index) => (
              <li
                key={customer.id}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(customer);
                }}
                onMouseEnter={() => setActive(index)}
                className={cx(
                  "cursor-pointer px-3 py-2",
                  index === active && "bg-app-background",
                )}
              >
                <div className="text-sm font-medium">
                  {customer.displayName}
                </div>
                <div className="text-xs text-app-muted">
                  {[
                    customer.number,
                    customer.billingAddress
                      ? `${customer.billingAddress.line1}, ${customer.billingAddress.city}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** Load a customer by id (for pages opened with ?customerId=…). */
export function useCustomer(id: string | null | undefined) {
  return useQuery({
    queryKey: ["customer", id],
    queryFn: () =>
      apiGet<{ data: CustomerResponse }>(`/customers/${id}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(id),
  });
}
